import React, { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import { getSeoShopUrl } from '@/lib/utilities/Utils'
import FreeSampleModal from '../free-sample-modal'
import CartCounter from '../cart-counter'

const getFlashSaleShopUrl = (product: Product): string => {
  const { shopUrl } = getSeoShopUrl(
    'india',
    'mumbai',
    'colombo-10',
    'groceries',
    product.storeName,
    product.storeId
  )
  return shopUrl
}

interface Product {
  retailerProductId: number
  productName: string
  mrp: number
  sellingPrice: number
  imageUrl: string
  measurementDispayText: string
  flashSaleExpires: string
  flashSaleTitle: string
  flashBGColor: string
  flashTitleColor: string
  isFreeSample: boolean
  offerTag: string
  flashSaleHeaderBGColor?: string[]
  storeId: number
  storeName: string
  isFlashSaleProduct: boolean
}

interface Props {
  product: Product
  fromShopDetails?: boolean
}

const FlashSaleProductCard: React.FC<Props> = ({
  product,
  fromShopDetails = false
}) => {
  const {
    productName,
    mrp,
    sellingPrice,
    imageUrl,
    measurementDispayText,
    flashSaleExpires,
    flashSaleTitle,
    flashBGColor,
    flashTitleColor,
    isFreeSample,
    offerTag,
    flashSaleHeaderBGColor
  } = product

  const router = useRouter()
  const cartItems = useSelector((state: any) => state?.commonData?.cartItems)
  const cartProduct = cartItems?.[product?.retailerProductId ?? 0]
  const url = getFlashSaleShopUrl(product)

  const quantity =
    cartProduct !== null && cartProduct?.storeId === product?.storeId
      ? cartProduct?.quantity
      : 0
  const [showModal, setShowModal] = useState(false)
  const [timeLeft, setTimeLeft] = useState('')

  const parseUTCDate = (date: string): Date => {
    return new Date(`${date}Z`)
  }

  const padTime = (value: number): string => {
    return value.toString().padStart(2, '0')
  }

  const getTimeLeft = (): string => {
    const expiryTime = parseUTCDate(flashSaleExpires).getTime()
    const diff = Math.max(0, expiryTime - Date.now())

    const totalMinutes = Math.floor(diff / 60000)

    const days = Math.floor(totalMinutes / 1440)
    const hours = Math.floor((totalMinutes % 1440) / 60)
    const mins = totalMinutes % 60

    if (days > 0) {
      return `${days}d : ${padTime(hours)}h`
    }

    return `${padTime(hours)}h : ${padTime(mins)}m`
  }

  useEffect(() => {
    const updateTimer = (): void => {
      setTimeLeft(getTimeLeft())
    }

    updateTimer()

    const interval = setInterval(updateTimer, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [flashSaleExpires])

  const headerStyle =
    flashSaleHeaderBGColor != null && flashSaleHeaderBGColor.length >= 2
      ? {
          background: `linear-gradient(to right, ${flashSaleHeaderBGColor[0]}, ${flashSaleHeaderBGColor[1]})`,
          color: flashTitleColor
        }
      : {
          backgroundColor: flashBGColor,
          color: flashTitleColor
        }

  const notifyCarouselInteraction = (): void => {
    window.dispatchEvent(new Event('flash-carousel-interaction'))
  }

  return (
    <>
      {showModal && (
        <FreeSampleModal
          product={product}
          onClose={() => {
            setShowModal(false)
          }}
          onConfirm={() => {
            router.push(url).catch(() => {})
            setShowModal(false)
          }}
        />
      )}

      <div className="w-dvw max-w-sm rounded-2xl shadow-lg overflow-hidden">
        <div
          className="flex justify-between items-center px-4 py-[18px] rounded-xl"
          style={headerStyle}
        >
          <span className="pb-[10px] text-lg font-bold px-2">{flashSaleTitle ?? 'Deal of the day'}</span>
          <span className="pb-[10px] text-lg font-bold">{timeLeft}</span>
        </div>

        <div className="relative z-5 flex items-center gap-3 p-4 bg-white rounded-2xl -mt-5 shadow-md">

          <div className="absolute top-0 left-[-4px] w-11 h-11 bg-[url('/discount-badge.png')] bg-contain bg-no-repeat">
            <div
              className="absolute inset-0 flex flex-col items-center justify-center text-white font-extrabold leading-[9px] text-center px-[2px]">
              {isFreeSample
                ? (
                  <span className="text-[10px] leading-none pr-1">FREE</span>
                  )
                : (
                  <>
                    <span className='text-[10px] pr-1'>{offerTag?.split(' ')[0]}</span>
                    <span className="text-[9px] pr-1">OFF</span>
                  </>
                  )}
            </div>
          </div>
          <div className="w-[104px] h-[104px] flex-shrink-0">
            <img
              src={imageUrl}
              alt={productName}
              className="w-full h-full object-contain"
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold truncate">
              {productName}
            </div>

            <div className="flex items-center gap-2 mt-1">
              {mrp > 0 && (
                <span className="text-gray-600 line-through text-xs">
                  ₹{mrp}
                </span>
              )}
              <span className="text-[14px] font-bold text-black">
                ₹{sellingPrice}
              </span>
            </div>

            <div className="flex items-center justify-between mt-3 gap-3">
              <div className="flex-1 border border-[#D0D0D0] px-2 py-1 text-sm rounded-lg text-center text-[#787878]">
                {measurementDispayText}
              </div>

             {isFreeSample
               ? (
                    <button
                        onClick={() => {
                          notifyCarouselInteraction()
                          if (fromShopDetails) {
                            setShowModal(true)
                          } else {
                            router.push(url).catch(() => {})
                          }
                        }}
                        className="flex-1 border border-pink-600 text-pink-600 px-2 py-1 rounded-lg text-sm font-semibold"
                    >
                    Shop
                  </button>
                 )
               : (
                  <div className="flex-1">
                    <CartCounter
                        product={product}
                        quantity={quantity}
                        inStock={true}
                        handleNotify={notifyCarouselInteraction}
                        url= {url}
                        productId={product?.retailerProductId}
                    />
                  </div>
                 )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default FlashSaleProductCard
